import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, SERVICES, INDUSTRIES, FOOTER_LINKS } from "../data/site";
import { subscribeNewsletter } from "../lib/api";
import { trackEvent } from "../lib/analytics";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribeNewsletter({ email });
      toast.success("You're subscribed. Thank you for joining our list.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-charcoal text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <img
                src={COMPANY.logo}
                alt="Silvertree Solutions logo"
                className="h-14 w-auto bg-white rounded-md p-1.5"
              />
              <img
                src={COMPANY.wordmark}
                alt="Silvertree Solutions"
                className="h-10 w-auto"
                data-testid="footer-wordmark"
              />
            </div>
            <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-sm">
              {COMPANY.promise}
            </p>
            <div className="mt-6 space-y-3 text-sm text-white/80">
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 hover:text-white" data-testid="footer-email" onClick={() => trackEvent("email_click", { page_path: window.location.pathname, cta_location: "footer" })}>
                <Mail className="h-4 w-4 text-gold" /> {COMPANY.email}
              </a>
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 hover:text-white" data-testid="footer-phone" onClick={() => trackEvent("phone_click", { page_path: window.location.pathname, cta_location: "footer" })}>
                <Phone className="h-4 w-4 text-gold" /> {COMPANY.phone}
              </a>
              <span className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-gold" /> {COMPANY.hq}
              </span>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Services</h4>
            <ul className="mt-5 space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm text-white/70 hover:text-white transition-colors">
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" className="text-sm text-gold hover:text-white transition-colors">
                  View all services
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Industries</h4>
            <ul className="mt-5 space-y-3">
              {INDUSTRIES.slice(0, 6).map((i) => (
                <li key={i.slug}>
                  <Link to={`/industries/${i.slug}`} className="text-sm text-white/70 hover:text-white transition-colors">
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Company</h4>
            <ul className="mt-5 space-y-3">
              {FOOTER_LINKS.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-sm text-white/70 hover:text-white transition-colors">
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/assessments/it-assessment" className="text-sm text-white/70 hover:text-white transition-colors" data-testid="footer-it-assessment">
                  Technology Health Assessment
                </Link>
              </li>
              <li>
                <Link to="/assessments/ai-readiness" className="text-sm text-white/70 hover:text-white transition-colors" data-testid="footer-ai-assessment">
                  AI Readiness Assessment
                </Link>
              </li>
              <li>
                <Link to="/assessments/cybersecurity" className="text-sm text-white/70 hover:text-white transition-colors" data-testid="footer-cyber-assessment">
                  Cybersecurity Assessment
                </Link>
              </li>
            </ul>
            <form onSubmit={handleSubscribe} className="mt-8" data-testid="newsletter-form">
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Newsletter</h4>
              <div className="mt-4 flex items-center border border-white/20 rounded-md overflow-hidden bg-white/5">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-transparent px-3 py-2.5 text-sm text-white placeholder:text-white/55 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit"
                  className="px-3 py-2.5 bg-forest hover:bg-forest-dark transition-colors"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-white/60">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Use</Link>
            <Link to="/portal/login" className="hover:text-white" data-testid="footer-client-portal">Client Portal</Link>
            <a href={COMPANY.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-white" data-testid="footer-linkedin">
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
